import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchInput from '@components/common/SearchInput';
import { api } from '@/api/growth/farmland/api';
import {
    FarmlandListItem,
    FarmlandListRes,
    FarmlandListReq,
} from '@/api/growth/farmland/type';
import DataTable, {
    ColumnConfig,
} from '@components/common/DataTable/DataTable';
import { queryStringToObject, typeConverters } from '@utils/Utils';
import ActionButton from '@components/common/ActionButton/ActionButton';
import { Alert } from '@utils/alert';

interface FarmlandListProps {
    gsIdx?: number;
    onClickRow: (row?: FarmlandListItem) => void;
    onEditRow: (gfIdx: number) => void;
    onSuccess: () => void;
}

const FarmlandList = (propsData: FarmlandListProps) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    //const hasCalledOnClickRow = useRef(false); // 호출 여부를 추적
    // gsIdx는 props나 URL 파라미터에서 가져옴
    const gsIdx = propsData.gsIdx || Number(propsData.gsIdx);

    const [filters, setFilters] = useState<FarmlandListReq>(() => {
        const urlFilters = queryStringToObject<FarmlandListReq>(searchParams, {
            nowPage: typeConverters.number,
            pageSize: typeConverters.number,
        });

        return {
            gsIdx: gsIdx,
            pageSize: 10,
            nowPage: 1,
            pageGroup: 10,
            keyword: '',
            ...urlFilters,
        };
    });

    const updateFilter = (key: keyof FarmlandListReq, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            ...(key !== 'nowPage' && { nowPage: 1 }),
        }));
    };

    const handleSearch = (value: string) => {
        updateFilter('keyword', value);
    };

    const handlePageChange = (page: number) => {
        updateFilter('nowPage', page);
    };

    const handlePageSizeChange = (pageSize: number) => {
        updateFilter('pageSize', pageSize);
    };

    const [farmlandRes, setFarmlandRes] = useState<FarmlandListRes>();

    const fetchFarmlandList = useCallback(async () => {
        if (!gsIdx) return;

        const response = await api.farmlandList(filters);
        setFarmlandRes(response);
    }, [filters, gsIdx]);

    const handleClickRow = (row?: FarmlandListItem) => {
        propsData.onClickRow(row);
    };

    const CROP_IMAGE_MAP = {
        옥수수: {
            path: '/images/crop/corn.png',
        },
        감자: {
            path: '/images/crop/potato.png',
        },
        벼: {
            path: '/images/crop/rice.png',
        },
        콩: {
            path: '/images/crop/soybean.png',
        },
        잔디: {
            path: '/images/crop/bermudagrass.png',
        },
    } as const;

    const columns: ColumnConfig<FarmlandListItem>[] = [
        { key: 'gfIdx', header: 'No' },
        { key: 'farmlandName', header: '농지명', clickable: true },
        { key: 'address', header: '주소' },
        { key: 'area', header: '면적(㎡)' },
        { key: 'jimok', header: '지목' },
        {
            key: 'gcName',
            header: '작물명',
            render: value => {
                if (!value) return '-';

                const cropInfo =
                    CROP_IMAGE_MAP[value as keyof typeof CROP_IMAGE_MAP];

                if (!cropInfo) return value;

                return (
                    <div className="d-flex align-items-center gap-2">
                        <img
                            src={cropInfo.path}
                            alt={value}
                            style={{ width: '24px', height: '24px' }}
                        />
                        <span>{value}</span>
                    </div>
                );
            },
        },
        { key: 'guName', header: '품종명', render: value => value || '-' },
        {
            key: 'growthFlag',
            header: '생육관리',
            render: (_value, row) => {
                // growthFlag가 0이면 사용안함
                if (row.growthFlag === 0) {
                    return <span className="text-muted">미사용</span>;
                }

                // 생육관리상태에 따른 텍스트 매핑
                const statusTextMap: { [key: number]: string } = {
                    0: '대기',
                    1: '날씨데이터 갱신요청',
                    2: '날씨데이터 갱신완료',
                    3: '날씨파일 생성완료',
                    4: '토양파일 생성완료',
                    5: '파종일 추천',
                    6: '파종일 설정완료',
                    7: '생육관리중',
                    8: '수확완료',
                    9: '생육불가조건',
                    '-99': '비정상중단',
                };

                // growthStatus가 7(생육관리중) 또는 8(수확완료)일 때 버튼 표시
                if (row.growthStatus === 7 || row.growthStatus === 8) {
                    return (
                        <span
                            className="badge bg-info tul mcp"
                            onClick={() => handleGrowthManagement(row)}
                        >
                            <i className="ri-external-link-line"></i> 생육관리
                        </span>
                    );
                }

                // 그 외의 경우 상태 텍스트 표시
                return (
                    <span className="badge bg-danger">
                        {statusTextMap[row.growthStatus] || '알 수 없음'}
                    </span>
                );
            },
        },
        /*{ key: 'updDt', header: '갱신일자', render: value => value || '-' },*/
        {
            key: 'actions',
            header: '관리',
            clickable: false,
            render: (_value, row) => (
                <div className="d-flex gap-1 justify-content-center ">
                    <ActionButton<FarmlandListItem>
                        type="edit"
                        size="sm"
                        text="수정"
                        className="btn-warning"
                        icon="ri-edit-line"
                        onClick={() => handleEdit(row)}
                    />
                    <ActionButton<FarmlandListItem>
                        type="delete"
                        size="sm"
                        text="삭제"
                        className="btn-danger"
                        icon="ri-delete-bin-line"
                        onClick={() => handleDelete(row)}
                    />
                </div>
            ),
        },
    ];

    const handleGrowthManagement = (row: FarmlandListItem) => {
        navigate(`/growth/dashboard/${row.gfIdx}`);
    };

    // 액션 버튼 핸들러들
    const handleEdit = (row: FarmlandListItem) => {
        console.log('edit');
        propsData.onEditRow(row.gfIdx);
    };

    const handleDelete = async (row: FarmlandListItem) => {
        await Alert.confirmAndExecute(
            `[${row.farmlandName}] 농지를 삭제하시겠습니까?`,
            '삭제시 복구가 불가합니다. 주의해주세요.',
            true,
            async () => {
                await api.farmlandDelete(row.gfIdx).then(() => {
                    fetchFarmlandList();
                    propsData.onSuccess();
                });
            },
        );
    };

    useEffect(() => {
        if (gsIdx) {
            fetchFarmlandList();
        }
    }, [fetchFarmlandList, gsIdx]);

    // 목록이 있을때 첫번째항목 선택되도록
    // useEffect(() => {
    //     if (
    //         farmlandRes?.data &&
    //         farmlandRes.data.length > 0 &&
    //         !hasCalledOnClickRow.current
    //     ) {
    //         // 목록이 있을때
    //         // propsData.onClickRow(farmlandRes.data[0]);
    //         // hasCalledOnClickRow.current = true;
    //     } else if (
    //         farmlandRes?.data &&
    //         farmlandRes.data.length === 0 &&
    //         !hasCalledOnClickRow.current
    //     ) {
    //         // 데이터가 없을 때도 한 번만 호출
    //         //propsData.onClickRow();
    //         //hasCalledOnClickRow.current = true;
    //     }
    // }, [farmlandRes, propsData]);

    return (
        <>
            <div className="row">
                <div className="col-xl-12">
                    <div className="card custom-card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-12">
                                    <SearchInput
                                        placeholder="농지명, 주소, 작물명, 품종명을 입력해주세요."
                                        value={filters.keyword || ''}
                                        onSearch={handleSearch}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-xl-12">
                    <DataTable<FarmlandListItem>
                        data={farmlandRes?.data}
                        columns={columns}
                        paging={farmlandRes?.paging}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onClickRow={handleClickRow}
                    />
                </div>
            </div>
        </>
    );
};

export default FarmlandList;
